import { Injectable, inject } from '@angular/core';
import { combineLatest, map, Observable, of } from 'rxjs';
import { RegistrationType } from './types/registration-types';
import { COUNTRIES_REQUIRING_A2P_REGISTRATION, getCountriesRequiringRegulatoryCompliance } from '../constants';
import { OwnerService } from '../common/owner.service';
import { A2PRegistrationService } from './a2p-registration/a2p-registration.service';
import { RegulationService } from './regulatory-compliance/regulation.service';
import { RegistrationStage } from './a2p-registration/a2p-registration.constants';
import { RegulationStatus } from '@vendasta/protos/sms/v2/enums';

@Injectable({
  providedIn: 'root',
})
export class RegistrationService {
  private readonly ownerService = inject(OwnerService, { optional: true });
  private readonly a2pRegistrationService = inject(A2PRegistrationService);
  private readonly regulationService = inject(RegulationService);

  /**
   * Determines the registration type required based on the owner's country.
   */
  getRegistrationType$(): Observable<RegistrationType> {
    if (!this.ownerService) {
      return of(RegistrationType.NONE);
    }

    return this.ownerService.getOwnerLocation$().pipe(
      map((ownerLocation) => {
        const country = ownerLocation.country?.toLowerCase();

        if (!country) {
          return RegistrationType.NONE;
        }

        // Check if country requires A2P registration
        if (COUNTRIES_REQUIRING_A2P_REGISTRATION.includes(country)) {
          return RegistrationType.A2P_REGISTRATION;
        }

        // Check if country requires regulatory compliance
        const countriesRequiringRegulatory = getCountriesRequiringRegulatoryCompliance();
        if (countriesRequiringRegulatory.includes(country)) {
          return RegistrationType.REGULATORY_COMPLIANCE;
        }

        return RegistrationType.NONE;
      }),
    );
  }

  isRegistrationRequired$(): Observable<boolean> {
    return this.getRegistrationType$().pipe(map((registrationType) => registrationType !== RegistrationType.NONE));
  }

  /**
   * Get the current registration stage based on registration type and status
   * This combines both A2P and regulation status to determine the overall stage
   */
  getRegistrationStage$(): Observable<RegistrationStage> {
    return combineLatest([
      this.getRegistrationType$(),
      this.a2pRegistrationService.registrationStage$,
      this.regulationService.registrationStage$,
    ]).pipe(
      map(([registrationType, a2pStage, regulationStage]) => {
        switch (registrationType) {
          case RegistrationType.A2P_REGISTRATION:
            return a2pStage;
          case RegistrationType.REGULATORY_COMPLIANCE:
            return regulationStage;
          case RegistrationType.NONE:
          default:
            return RegistrationStage.RegistrationComplete; // No registration needed
        }
      }),
    );
  }

  getRegulationStatus$(): Observable<RegulationStatus> {
    return this.regulationService.regulationStatus$;
  }
}
